<template>
  <b-form class="toy-rent-close-modal-form" novalidate>
    <ToyRentDetails
      :toy="toy"
      compact
      hide-total
      @update-rent-amount="rentAmount = $event"
      @update-products-amount="productsAmount = $event"
    />

    <div class="close-modal-grid">
      <div>
        <b-alert
          v-if="shouldShowLoyaltyOption && !loyaltyPeriod"
          show
          variant="warning"
          class="compact-alert mb-2"
        >
          Para usar o cartao fidelidade, cadastre um periodo ativo de 12 min.
        </b-alert>

        <div v-if="shouldShowLoyaltyOption" class="loyalty-discount mb-2">
          <div class="d-flex align-items-center justify-content-between">
            <b-form-checkbox
              v-model="useLoyaltyDiscount"
              :disabled="!loyaltyPeriod"
            >
              Usar cartao fidelidade
            </b-form-checkbox>
            <strong v-if="loyaltyPeriod" class="text-success">
              -{{ loyaltyPeriod.price | formatCurrency }}
            </strong>
          </div>
          <small v-if="loyaltyPeriod" class="text-muted d-block mt-1">
            Produtos nao recebem desconto.
          </small>
        </div>

        <div class="rent-close-total mb-3">
          <div
            v-if="shouldShowRentBreakdown"
            class="d-flex justify-content-between"
          >
            <span>Aluguel</span>
            <strong>{{ normalizedRentAmount | formatCurrency }}</strong>
          </div>
          <div
            v-if="normalizedProductsAmount > 0"
            class="d-flex justify-content-between"
          >
            <span>Produtos</span>
            <strong>{{ normalizedProductsAmount | formatCurrency }}</strong>
          </div>
          <div
            v-if="loyaltyDiscountAmount > 0"
            class="d-flex justify-content-between text-success"
          >
            <span>Desconto</span>
            <strong>-{{ loyaltyDiscountAmount | formatCurrency }}</strong>
          </div>
          <div class="d-flex justify-content-between rent-close-total-final">
            <span>Total</span>
            <strong>{{ totalToPay | formatCurrency }}</strong>
          </div>
        </div>
      </div>

      <b-form-group
        label="Formas de Pagamento"
        label-for="payment-type"
        :class="paymentsValidationClass"
      >
        <div class="payment-methods-grid">
          <b-input-group
            v-for="payment in form.payments"
            :key="payment.type"
            size="sm"
            class="payment-line"
          >
            <b-input-group-text slot="prepend">
              <span>
                <span :class="payment.icon" class="mr-2" />
                {{ payment.name }}
              </span>
            </b-input-group-text>
            <b-form-input
              v-model="payment.amount"
              type="number"
              placeholder="R$ 0,00"
            />
          </b-input-group>
        </div>

        <b-form-invalid-feedback v-if="$v.form.payments.$invalid">
          <span v-if="!$v.form.payments.paymentMethod">
            Informe pelo menos uma forma de pagamento
          </span>
          <span v-else-if="!$v.form.payments.totalAmount">
            O valor a ser pago e diferente do valor total do aluguel
          </span>
        </b-form-invalid-feedback>
      </b-form-group>
    </div>
  </b-form>
</template>

<script>
import withFormValidation from '@/mixins/withFormValidation'
import { PAYMENT_METHODS } from '@/constants'
import { formatCurrency } from '@/helpers/numbers'
import ToyRentDetails from './ToyRentDetails'

const LOYALTY_REQUIRED_POINTS = 10
const LOYALTY_DISCOUNT_MINUTES = 12

export default {
  name: 'ToyRentCloseModalForm',
  components: {
    ToyRentDetails
  },
  filters: {
    formatCurrency
  },
  mixins: [withFormValidation({ fields: ['payments'] })],
  validations: {
    form: {
      payments: {
        paymentMethod: (value) =>
          (value || []).some(({ amount }) => amount > 0),
        totalAmount(value) {
          const paymentsAmount = (value || []).reduce(
            (acc, { amount }) => acc + Number(amount || 0),
            0
          )
          return Math.abs(paymentsAmount - this.totalToPay) <= 0.01
        }
      }
    }
  },
  props: {
    toy: {
      type: Object,
      required: true
    },
    storePeriods: {
      type: Array,
      default: () => []
    },
    loyaltyDiscountEnabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      rentAmount: 0,
      productsAmount: 0,
      useLoyaltyDiscount: Boolean(this.loyaltyDiscountEnabled)
    }
  },
  computed: {
    form: {
      set(value) {
        this.$emit('input', value)
      },
      get() {
        return this.value
      }
    },
    paymentsValidationClass() {
      const { $dirty, $invalid } = this.$v.form.payments
      return {
        'is-invalid': $dirty && $invalid
      }
    },
    normalizedRentAmount() {
      return Number(this.rentAmount || 0)
    },
    normalizedProductsAmount() {
      return Number(this.productsAmount || 0)
    },
    loyaltyPoints() {
      return Number(this.toy.rent?.customer?.points?.total_rents_closed || 0)
    },
    elapsedTime() {
      return Number(this.toy.rent?.elapsedTime || 0)
    },
    shouldShowLoyaltyOption() {
      return (
        this.loyaltyPoints >= LOYALTY_REQUIRED_POINTS &&
        this.elapsedTime > LOYALTY_DISCOUNT_MINUTES
      )
    },
    loyaltyPeriod() {
      return (this.storePeriods || []).find(
        (period) =>
          Number(period.time) === LOYALTY_DISCOUNT_MINUTES &&
          period.status === 'active'
      )
    },
    canUseLoyaltyDiscount() {
      return Boolean(this.shouldShowLoyaltyOption && this.loyaltyPeriod)
    },
    loyaltyDiscountAmount() {
      if (!this.useLoyaltyDiscount || !this.canUseLoyaltyDiscount) return 0
      return Math.min(
        Number(this.loyaltyPeriod.price || 0),
        this.normalizedRentAmount
      )
    },
    totalBeforeDiscount() {
      return this.normalizedRentAmount + this.normalizedProductsAmount
    },
    totalToPay() {
      return this.totalBeforeDiscount - this.loyaltyDiscountAmount
    },
    shouldShowRentBreakdown() {
      return this.normalizedProductsAmount > 0 || this.loyaltyDiscountAmount > 0
    }
  },
  watch: {
    loyaltyDiscountEnabled(value) {
      this.useLoyaltyDiscount = Boolean(value)
    },
    useLoyaltyDiscount(value) {
      this.emitLoyaltyDiscountChange(value)
    },
    loyaltyPeriod(value) {
      if (!value) {
        this.useLoyaltyDiscount = false
      }
    },
    canUseLoyaltyDiscount(value) {
      if (!value) {
        this.useLoyaltyDiscount = false
      }
    }
  },
  created() {
    this.PAYMENT_METHODS = PAYMENT_METHODS
  },
  methods: {
    formatCurrency,
    emitLoyaltyDiscountChange(value) {
      this.$emit('loyalty-discount-change', {
        rentId: this.toy.rent.id,
        enabled: Boolean(value && this.canUseLoyaltyDiscount)
      })
    },
    handleSubmit() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) return

      this.$emit('submit', {
        ...this.form,
        loyaltyDiscount: {
          enabled: Boolean(
            this.useLoyaltyDiscount && this.canUseLoyaltyDiscount
          ),
          grossRentAmount: this.normalizedRentAmount,
          productsAmount: this.normalizedProductsAmount
        }
      })
    }
  }
}
</script>

<style lang="scss">
.toy-rent-close-modal-form {
  .form-group {
    margin-bottom: 0.75rem;
  }

  legend {
    font-size: 1rem;
    font-weight: 700;
    margin-bottom: 0.45rem;
  }

  .close-modal-grid {
    display: grid;
    gap: 0.75rem;
    grid-template-columns: minmax(210px, 0.85fr) minmax(230px, 1.15fr);
  }

  .input-group-text {
    min-width: 106px;
    padding-bottom: 0.35rem;
    padding-top: 0.35rem;
  }

  .form-control {
    min-height: 32px;
    padding-bottom: 0.25rem;
    padding-top: 0.25rem;
  }

  .payment-methods-grid {
    display: grid;
    gap: 0.5rem;
    grid-template-columns: 1fr;
  }

  .payment-line {
    margin-bottom: 0;
  }

  .loyalty-discount,
  .rent-close-total {
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    padding: 0.6rem 0.75rem;
  }

  .compact-alert {
    padding: 0.5rem 0.75rem;
  }

  .rent-close-total-final {
    border-top: 1px solid #dee2e6;
    font-size: 1.05rem;
    margin-top: 0.35rem;
    padding-top: 0.35rem;
  }
}

@media (max-width: 767.98px) {
  .toy-rent-close-modal-form {
    .close-modal-grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
